/*
 * Sidebar structure ("sidebar A"): Home fixed on top, three groups, a Pinned group, then More.
 * Pure data + functions (icons live in icons.js) so grouping, gating and pins are testable.
 * Routes are today's routes; only the labels of Dashboard/Contacts/Accounts changed.
 */
import { safeGet, safeSet } from "./navBadges";

export const NAV_ITEMS = {
  home: { path: "/dashboard", title: "Home" },
  listBuilder: { path: "/list-builder", title: "List Builder", caption: "People · Companies" },
  contacts: { path: "/contacts", title: "My contacts" },
  accounts: { path: "/accounts", title: "My accounts" },
  signals: { path: "/signals", title: "Signals", caption: "My signals · Library" },
  sequences: { path: "/sequences", title: "Sequences" },
  inbox: { path: "/inbox", title: "Inbox" },
  tasks: { path: "/tasks", title: "Tasks" },
  agents: { path: "/agents", title: "Agents", caption: "Templates · Approvals · Runs", isNew: true },
  // Everything else lives in More (pinnable).
  copilot: { path: "/copilot", title: "Copilot", desc: "Ask anything, find people, draft and plan with AI" },
  templates: { path: "/templates", title: "Templates", desc: "Save and reuse your best email, LinkedIn and call content" },
  calls: { path: "/calls", title: "Calls", desc: "Make, schedule and record your calls" },
  dataToolkit: { path: "/data-toolkit", title: "Data Toolkit", desc: "Enrich, verify and clean contact data in bulk" },
  reports: { path: "/reports", title: "Reports", desc: "See how your team and sequences are performing" },
  team: { path: "/settings/team", title: "Team", desc: "Invite and manage your team" },
  pricing: { path: "/pricing", title: "Pricing", desc: "See the plans available for your subscription" },
  settings: { path: "/settings", title: "Settings", desc: "Manage your account, inboxes and preferences" }
};

export const HOME = "home";

export const NAV_GROUPS = [
  { id: "prospect", title: "Prospect", items: ["listBuilder", "contacts", "accounts", "signals"] },
  { id: "engage", title: "Engage", items: ["sequences", "inbox", "tasks"] },
  { id: "automate", title: "Automate", items: ["agents"] }
];

export const MORE_ITEMS = ["copilot", "templates", "calls", "dataToolkit", "reports", "team", "pricing", "settings"];

// Today's gating, unchanged: agents behind the flag, team for team admins, pricing hidden for a
// paid member who isn't the team admin (DashboardRouting drops that route too).
export const isItemVisible = (name, { agentsEnabled = false, signalsEnabled = false, adminTeamUser = false, subscriptionPlanId = "" } = {}) => {
  if (!NAV_ITEMS[name]) return false;
  if (name === "agents") return !!agentsEnabled;
  if (name === "signals") return !!signalsEnabled;
  if (name === "team") return !!adminTeamUser;
  if (name === "pricing") return !(subscriptionPlanId && !adminTeamUser);
  return true;
};

// The groups as shown: hidden items dropped, empty groups dropped; pinned More items form their own group.
export const buildNav = (gates = {}, pins = []) => {
  const groups = NAV_GROUPS.map(g => ({ ...g, items: g.items.filter(n => isItemVisible(n, gates)) })).filter(
    g => g.items.length
  );
  const pinned = (pins || []).filter(n => MORE_ITEMS.includes(n) && isItemVisible(n, gates));
  if (pinned.length) groups.push({ id: "pinned", title: "Pinned", items: pinned });
  return { home: HOME, groups, more: MORE_ITEMS.filter(n => isItemVisible(n, gates)) };
};

export const togglePin = (pins = [], name) =>
  pins.includes(name) ? pins.filter(n => n !== name) : [...pins, name];

// Pins are per member, per browser. ponytail: localStorage only; cross-device needs a server-side member preference.
const pinsKey = memberId => `sgSideNavPins:${memberId || "me"}`;
export const readPins = memberId => {
  try {
    const list = JSON.parse(safeGet(pinsKey(memberId)) || "[]");
    return Array.isArray(list) ? list.filter(n => MORE_ITEMS.includes(n)) : [];
  } catch (e) {
    return [];
  }
};
export const writePins = (memberId, pins) => safeSet(pinsKey(memberId), JSON.stringify(pins || []));

const COLLAPSED_KEY = "sgSideNavCollapsedGroups";
export const readCollapsedGroups = () => {
  try {
    const v = JSON.parse(safeGet(COLLAPSED_KEY) || "{}");
    return v && typeof v === "object" && !Array.isArray(v) ? v : {};
  } catch (e) {
    return {};
  }
};
export const writeCollapsedGroups = v => safeSet(COLLAPSED_KEY, JSON.stringify(v || {}));
export const toggleGroup = (collapsed = {}, id) => ({ ...collapsed, [id]: !collapsed[id] });
